// src/components/coldcalendar/ColdCalendar.jsx
import React, { useState, useEffect, useMemo } from 'react';

import { StandardHeader } from '../../components/common/StandardHeader';
import ReportingTestTable from '../../components/coldcalendar/sections/ReportingTableTemplate';
import CollapsibleSection from '../../components/common/CollapsibleSection';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import NoDataMessage from '../../components/common/NoDataMessage';
import { useColdCalendarData } from '../../hooks/useColdCalendarData';
import { calculateSummaryMetrics, formatLastUpdated } from '../../utils/dataUtils';

const ColdCalendar = () => {
  const {
    data: summaryData,
    loading: summaryLoading,
    refreshing: summaryRefreshing,
    error: summaryError,
    refetch: summaryRefetch,
    lastUpdated: summaryLastUpdated,
    initialized: summaryInitialized,
  } = useColdCalendarData({
    group_by_column: 'reporting_title',
    webinar_filter: [],
    test_filter: [],
    version_filter: [],
    start_date: '',
    end_date: '',
    search_term: '',
  });

  const [titleCollapsed,       setTitleCollapsed]       = useState(false);
  const [descriptionCollapsed, setDescriptionCollapsed] = useState(false);
  const [accountCollapsed,     setAccountCollapsed]     = useState(false);

  useEffect(() => {
    document.title = 'Pulse: Cold Calendar';
  }, []);

  const summaryMetrics = useMemo(() => calculateSummaryMetrics(summaryData), [summaryData]);

  const recordsBadge = summaryData?.rows?.length > 0 ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-400 text-darkblue-500 border border-teal-300">
      {summaryData.rows.length} records
    </span>
  ) : null;

  if ((summaryLoading && !summaryInitialized) || (summaryRefreshing && !summaryData?.rows?.length)) {
    return <LoadingSpinner />;
  }

  if (summaryError && !summaryData?.rows?.length) {
    return <ErrorMessage message={summaryError} onRetry={summaryRefetch} />;
  }

  if (summaryInitialized && !summaryLoading && !summaryRefreshing && !summaryData?.rows?.length) {
    return <NoDataMessage onRetry={summaryRefetch} />;
  }

  return (
    <div className="min-h-screen bg-grey-50">
      <div className="max-w-full mx-auto space-y-4">

        <StandardHeader
          title="Cold Calendar Testing"
          subtitle="Performance analysis for webinar A/B testing campaigns"
          isRefreshing={summaryRefreshing}
          onRefresh={summaryRefetch}
          refreshButtonText="Refresh"
          lastUpdated={summaryLastUpdated}
          customControls={recordsBadge}
        />

        {summaryRefreshing && (
          <div className="bg-brightblue-50 border border-brightblue-200 rounded-lg px-4 py-2.5 flex items-center justify-center gap-2">
            <div className="w-3.5 h-3.5 border-2 border-brightblue-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-brightblue-700">Refreshing data…</span>
          </div>
        )}

        {summaryError && summaryData?.rows?.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-yellow-700">Error refreshing data: {summaryError}</span>
            <button onClick={summaryRefetch} className="text-xs text-yellow-700 hover:text-yellow-900 underline font-medium">Retry</button>
          </div>
        )}

        <CollapsibleSection
          title="Performance by Reporting Title"
          subtitle="Detailed performance metrics by test titles"
          collapsed={titleCollapsed}
          setCollapsed={setTitleCollapsed}
        >
          <ReportingTestTable tableType="title" />
        </CollapsibleSection>

        <CollapsibleSection
          title="Performance by Reporting Description"
          subtitle="Detailed performance metrics by test descriptions"
          collapsed={descriptionCollapsed}
          setCollapsed={setDescriptionCollapsed}
        >
          <ReportingTestTable tableType="description" />
        </CollapsibleSection>

        <CollapsibleSection
          title="Performance by Reporting Account"
          subtitle="Detailed performance metrics by test accounts"
          collapsed={accountCollapsed}
          setCollapsed={setAccountCollapsed}
        >
          <ReportingTestTable tableType="account" />
        </CollapsibleSection>
      </div>
    </div>
  );
};

export default ColdCalendar;